import { Download, Print, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Pagination,
  Select,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { SelectChangeEvent } from "@mui/material/Select";
import { DatePicker } from "@mui/x-date-pickers";
import { IResourceComponentsProps } from "@refinedev/core";
import html2pdf from "html2pdf.js";
import React, { useEffect, useMemo, useState } from "react";
import * as ReactDOM from "react-dom/client";
import { useTranslation } from "react-i18next";
import { Currency } from "react-intl-number-format";
import { useQuery } from "@tanstack/react-query";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import {
  CreditReportParams,
  CreditReportResponse,
  CreditTransaction,
  getCreditReports,
} from "services/credits_v2";
import { getClients } from "services/client";
import { PDFReport } from "../../components/reports/PDFReport";

export const CreditsReport: React.FC<IResourceComponentsProps> = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const getDaysSince = (
    dateValue: string | Date | null | undefined,
  ): number | null => {
    if (!dateValue) return null;
    const raw = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(raw.getTime())) return null;
    // Compare at local day precision to avoid timezone/hour drift
    const a = new Date(
      raw.getFullYear(),
      raw.getMonth(),
      raw.getDate(),
    ).getTime();
    const now = new Date();
    const b = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    ).getTime();
    const diffMs = b - a;
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  };

  const getAgeColors = (daysSince: number | null) => {
    if (daysSince === null) {
      return {
        border: alpha(theme.palette.grey[500], 0.45),
        bg: alpha(theme.palette.grey[500], 0.14),
        stripe: theme.palette.grey[500],
        text: theme.palette.text.primary,
      };
    }
    if (daysSince < 3) {
      return {
        border: alpha(theme.palette.success.main, 0.75),
        bg: alpha(theme.palette.success.main, 0.22),
        stripe: theme.palette.success.main,
        text: theme.palette.text.primary,
      };
    }
    if (daysSince <= 6) {
      return {
        border: alpha(theme.palette.warning.main, 0.75),
        bg: alpha(theme.palette.warning.main, 0.22),
        stripe: theme.palette.warning.main,
        text: theme.palette.text.primary,
      };
    }
    return {
      border: alpha(theme.palette.error.main, 0.75),
      bg: alpha(theme.palette.error.main, 0.22),
      stripe: theme.palette.error.main,
      text: theme.palette.text.primary,
    };
  };

  // State for filters
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
  ); // Default to two days ago
  const [endDate, setEndDate] = useState<Date | null>(new Date()); // Default to current date
  const [clientFilter, setClientFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter] = useState<string>("");
  const [groupBy] = useState<string>("month");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(12);
  const [paginationMeta, setPaginationMeta] = useState<{
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  }>({
    page: 1,
    pageSize: 12,
    totalItems: 0,
    totalPages: 0,
  });
  const [showTransactions, setShowTransactions] = useState<boolean>(true);
  const [exportingPdf, setExportingPdf] = useState<boolean>(false);

  // State for API data
  const [loading, setLoading] = useState<boolean>(false);
  const [reportData, setReportData] = useState<CreditReportResponse | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  // Helper function to format date without timezone conversion
  const formatDateForAPI = (date: Date | null): string | undefined => {
    if (!date) return undefined;

    // Create a new date object to avoid mutating the original
    const localDate = new Date(date);

    // Get the local date components
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, "0");
    const day = String(localDate.getDate()).padStart(2, "0");

    // Return in YYYY-MM-DD format (local date, no timezone conversion)
    return `${year}-${month}-${day}`;
  };

  const { data: clientsData = [] } = useQuery({
    queryKey: ["credit-report-clients"],
    queryFn: getClients(),
    staleTime: 5 * 60 * 1000,
  });

  const clientAutocompleteOptions = useMemo(() => {
    const clientOptions = Array.isArray(clientsData) ? clientsData : [];
    return clientOptions.map((client: any) => ({
      label: `${client.name}${client.pseudo ? ` (${client.pseudo})` : ""}`,
      value: String(client.id),
    }));
  }, [clientsData]);
  const clientPhoneById = useMemo(() => {
    const source = Array.isArray(clientsData) ? clientsData : [];
    return source.reduce<Record<string, string>>((acc, client: any) => {
      const id = String(client?.id ?? "");
      if (!id) return acc;
      const phone = client?.phone;
      if (
        phone !== undefined &&
        phone !== null &&
        String(phone).trim() !== ""
      ) {
        acc[id] = String(phone);
      }
      return acc;
    }, {});
  }, [clientsData]);
  const selectedClientOption = useMemo(
    () =>
      clientAutocompleteOptions.find((opt) => opt.value === clientFilter) ||
      null,
    [clientAutocompleteOptions, clientFilter],
  );

  // Function to fetch report data
  const fetchReportData = async (
    overrides: { page?: number; pageSize?: number } = {},
  ) => {
    setLoading(true);
    setError(null);

    try {
      const requestedPage = overrides.page ?? page;
      const requestedPageSize = overrides.pageSize ?? pageSize;

      const params: CreditReportParams = {
        from: formatDateForAPI(startDate),
        to: formatDateForAPI(endDate),
        groupBy: groupBy as any,
        clientId: clientFilter ? parseInt(clientFilter) : undefined,
        status: (statusFilter as any) || undefined,
        type: (typeFilter as any) || undefined,
        page: requestedPage,
        pageSize: requestedPageSize,
      };

      const data = await getCreditReports(params);
      setReportData(data);

      const paginationFromApi = data.pagination;
      if (paginationFromApi) {
        setPaginationMeta(paginationFromApi);
        setPage(paginationFromApi.page);
        setPageSize(paginationFromApi.pageSize);
      } else {
        const fallbackTotalItems = Array.isArray(data?.data)
          ? data.data.length
          : 0;
        const fallbackTotalPages =
          fallbackTotalItems === 0
            ? 0
            : Math.ceil(fallbackTotalItems / requestedPageSize);
        setPaginationMeta({
          page: requestedPage,
          pageSize: requestedPageSize,
          totalItems: fallbackTotalItems,
          totalPages: fallbackTotalPages,
        });
        setPage(requestedPage);
        setPageSize(requestedPageSize);
      }
    } catch (err) {
      console.error("Error fetching credit report data:", err);
      setError("Failed to load report data. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void fetchReportData();
    // Intentionally run once on mount; user applies filters via Search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle search button click
  const handleSearch = () => {
    setPage(1);
    fetchReportData({ page: 1 });
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    if (value === page) return;
    setPage(value);
    fetchReportData({ page: value });
  };

  const handlePageSizeChange = (event: SelectChangeEvent) => {
    const newSize = Number(event.target.value) || pageSize;
    if (newSize === pageSize) return;
    setPageSize(newSize);
    setPage(1);
    fetchReportData({ page: 1, pageSize: newSize });
  };

  const handleToggleTransactions = () => {
    setShowTransactions((prev) => !prev);
  };

  // Handle PDF export — fetch full report (forExport) so PDF matches summary, not current page only
  const handleExportPDF = async () => {
    setExportingPdf(true);
    const tempDiv = document.createElement("div");
    let root: ReturnType<typeof ReactDOM.createRoot> | null = null;
    try {
      const params: CreditReportParams = {
        from: formatDateForAPI(startDate),
        to: formatDateForAPI(endDate),
        groupBy: groupBy as CreditReportParams["groupBy"],
        clientId: clientFilter ? parseInt(clientFilter, 10) : undefined,
        status: (statusFilter as CreditReportParams["status"]) || undefined,
        type: (typeFilter as CreditReportParams["type"]) || undefined,
        forExport: true,
      };
      const exportData = await getCreditReports(params);
      const rows = Array.isArray(exportData.data)
        ? exportData.data.filter(Boolean).map((row: any) => {
            if (Array.isArray(row?.transactions)) {
              return {
                ...row,
                transactions: row.transactions.map((transaction: any) => ({
                  ...transaction,
                  clientPhone:
                    transaction?.clientPhone ??
                    clientPhoneById[String(transaction?.clientId ?? "")] ??
                    "",
                })),
              };
            }
            return {
              ...row,
              clientPhone:
                row?.clientPhone ??
                clientPhoneById[String(row?.clientId ?? "")] ??
                "",
            };
          })
        : [];

      document.body.appendChild(tempDiv);
      root = ReactDOM.createRoot(tempDiv);
      root.render(
        <PDFReport
          data={{
            summary: exportData.summary,
            data: rows as Parameters<typeof PDFReport>[0]["data"]["data"],
          }}
          startDate={startDate}
          endDate={endDate}
          type="credits"
        />,
      );

      await new Promise((resolve) => setTimeout(resolve, 800));

      const opt = {
        margin: [0.3, 0.3],
        filename: `credits-report-${startDate?.toISOString().split("T")[0]}-${
          endDate?.toISOString().split("T")[0]
        }.pdf`,
        image: { type: "jpeg" as const, quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: true,
          letterRendering: true,
        },
        jsPDF: {
          unit: "in" as const,
          format: "a4" as const,
          orientation: "portrait" as const,
        },
      };

      await html2pdf().set(opt).from(tempDiv).save();
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      root?.unmount();
      if (tempDiv.parentNode) {
        document.body.removeChild(tempDiv);
      }
      setExportingPdf(false);
    }
  };

  // No fallback dummy data - we'll use proper error handling instead

  // Get summary data from API with empty defaults if not available
  const summary = reportData?.summary || {
    totalTransactions: 0,
    totalAmount: 0,
    paidCount: 0,
    unpaidCount: 0,
    creditAmount: 0,
    paidCreditAmount: 0,
    unpaidCreditAmount: 0,
    debitAmount: 0,
    netAmount: 0,
    clientCount: 0,
    typeBreakdown: {
      credit: 0,
      debit: 0,
    },
  };

  const creditData = useMemo(() => reportData?.data ?? [], [reportData]);
  const pagination = reportData?.pagination ?? paginationMeta;
  const showingRangeStart =
    pagination.totalItems === 0
      ? 0
      : (pagination.page - 1) * pagination.pageSize + 1;
  const showingRangeEnd =
    pagination.totalItems === 0
      ? 0
      : Math.min(pagination.page * pagination.pageSize, pagination.totalItems);

  // Chart data for transaction types
  const typeChartData = useMemo(
    () => [
      { name: t("Credits"), value: summary.creditAmount },
      { name: t("Debits"), value: summary.debitAmount },
    ],
    [summary.creditAmount, summary.debitAmount, t],
  );

  // Colors for charts
  const COLORS = [
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
  ];

  // Define client summary interface
  interface ClientSummary {
    name: string;
    pseudo?: string;
    phone?: string;
    totalCredit: number;
    totalPaid: number;
    totalUnpaid: number;
    totalDebit: number;
  }

  // Extract transactions from the API response based on grouping structure
  const transactions = useMemo(() => {
    if (!Array.isArray(creditData) || creditData.length === 0) {
      return [] as CreditTransaction[];
    }

    if (Array.isArray((creditData as any[])[0]?.transactions)) {
      return (creditData as any[]).flatMap(
        (group: any) => group.transactions || [],
      ) as CreditTransaction[];
    }

    return creditData as CreditTransaction[];
  }, [creditData]) as CreditTransaction[];

  const clientSummary = useMemo(() => {
    return transactions.reduce<Record<string, ClientSummary>>((acc, item) => {
      const clientId = String(item.clientId || "unknown");
      if (!acc[clientId]) {
        acc[clientId] = {
          name: item.clientName || "Unknown",
          pseudo: item.clientPseudo || "",
          phone:
            item.clientPhone?.toString() ?? clientPhoneById[clientId] ?? "",
          totalCredit: 0,
          totalPaid: 0,
          totalUnpaid: 0,
          totalDebit: 0,
        };
      }

      if (item.type === "credit") {
        acc[clientId].totalCredit += item.amount;
        if (item.status === "paid") {
          acc[clientId].totalPaid += item.amount;
        } else {
          acc[clientId].totalUnpaid += item.amount;
        }
      } else if (item.type === "debit") {
        acc[clientId].totalDebit += item.amount;
      }

      return acc;
    }, {});
  }, [clientPhoneById, transactions]);

  const hasData =
    summary.totalTransactions > 0 ||
    summary.totalAmount > 0 ||
    creditData.length > 0;

  if (!loading && !error && !hasData) {
    return (
      <Box p={5}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <DatePicker
                label={t("Start Date")}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label={t("End Date")}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={clientAutocompleteOptions}
                value={selectedClientOption}
                onChange={(_, newValue) =>
                  setClientFilter(newValue?.value ?? "")
                }
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("Client")}
                    placeholder={t("Search client")}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("Status")}</InputLabel>
                <Select
                  value={statusFilter}
                  label={t("Status")}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">{t("All")}</MenuItem>
                  <MenuItem value="paid">{t("Paid")}</MenuItem>
                  <MenuItem value="unpaid">{t("Unpaid")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<Search />}
                fullWidth
              >
                {t("Search")}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Typography variant="h6" gutterBottom textAlign="center">
          {t("No data found")}
        </Typography>
        <Typography variant="body1" color="textSecondary" textAlign="center">
          {t(
            "Try widening the date range or adjusting filters to see results.",
          )}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5">{t("Credits Report")}</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            startIcon={
              exportingPdf ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Print />
              )
            }
            onClick={handleExportPDF}
            disabled={exportingPdf}
          >
            {t("Print Report")}
          </Button>
          <Button
            variant="contained"
            startIcon={
              exportingPdf ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Download />
              )
            }
            onClick={handleExportPDF}
            disabled={exportingPdf}
          >
            {t("Export Report")}
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box p={3} id="credits-report-content">
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <DatePicker
                label={t("Start Date")}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label={t("End Date")}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Autocomplete
                fullWidth
                size="small"
                options={clientAutocompleteOptions}
                value={selectedClientOption}
                onChange={(_, newValue) =>
                  setClientFilter(newValue?.value ?? "")
                }
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("Client")}
                    placeholder={t("Search client")}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("Status")}</InputLabel>
                <Select
                  value={statusFilter}
                  label={t("Status")}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">{t("All")}</MenuItem>
                  <MenuItem value="paid">{t("Paid")}</MenuItem>
                  <MenuItem value="unpaid">{t("Unpaid")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Search />}
                onClick={handleSearch}
              >
                {t("Search")}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Summary Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  {t("Total Credits")}
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  <Currency locale="" currency="TND">
                    {summary.creditAmount}
                  </Currency>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  {t("Paid Credits")}
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  <Currency locale="en-US" currency="TND">
                    {summary.paidCreditAmount ?? 0}
                  </Currency>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  {t("Unpaid Credits")}
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="warning.main">
                  <Currency locale="en-US" currency="TND">
                    {summary.unpaidCreditAmount ?? 0}
                  </Currency>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  {t("Total Debits")}
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="info.main">
                  <Currency locale="en-US" currency="TND">
                    {summary.debitAmount}
                  </Currency>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts and Tables */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={12}>
            <Paper elevation={3} sx={{ p: 2, height: "100%" }}>
              <Typography variant="h6" gutterBottom>
                {t("Credits Distribution")}
              </Typography>
              <Box height={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={typeChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({
                        name,
                        percent,
                      }: {
                        name: string;
                        percent: number;
                      }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {typeChartData.map(
                        (
                          entry: { name: string; value: number },
                          index: number,
                        ) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ),
                      )}
                    </Pie>
                    <Tooltip
                      formatter={(value: number) => (
                        <Currency locale="en-US" currency="TND">
                          {value as number}
                        </Currency>
                      )}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={12}>
            <Paper elevation={3} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("Client Summary")}
              </Typography>
              <Grid container spacing={3}>
                {Object.values(clientSummary).map((client: any, index) => (
                  <Grid item xs={12} md={12} key={index}>
                    <Paper
                      elevation={2}
                      sx={{
                        p: 3,
                        height: "100%",
                        borderRadius: 3,
                        border: `1px solid ${alpha(
                          theme.palette.primary.main,
                          0.2,
                        )}`,
                        background:
                          theme.palette.mode === "dark"
                            ? alpha(theme.palette.background.paper, 0.6)
                            : "#fff",
                        boxShadow: `0 6px 18px ${alpha(
                          theme.palette.primary.main,
                          0.08,
                        )}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          color="text.primary"
                          gutterBottom
                        >
                          {client.name}{" "}
                          {client?.pseudo && `(${client?.pseudo})`}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          {t("Phone")}: {client.phone || "-"}
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">
                            {t("Total Credit")}
                          </Typography>
                          <Typography
                            variant="h5"
                            fontWeight={500}
                            color="text.primary"
                          >
                            <Currency locale="en-US" currency="TND">
                              {client.totalCredit}
                            </Currency>
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">
                            {t("Paid")}
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            color="success.main"
                            fontWeight={600}
                          >
                            <Currency locale="en-US" currency="TND">
                              {client.totalPaid}
                            </Currency>
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">
                            {t("Unpaid")}
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            color="warning.main"
                            fontWeight={600}
                          >
                            <Currency locale="en-US" currency="TND">
                              {client.totalUnpaid}
                            </Currency>
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">
                            {t("Debit")}
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            color="info.main"
                            fontWeight={600}
                          >
                            <Currency locale="en-US" currency="TND">
                              {client.totalDebit}
                            </Currency>
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={4}
          mb={2}
        >
          <Typography variant="h6">{t("Credits Transactions")}</Typography>
          <Button variant="outlined" onClick={handleToggleTransactions}>
            {showTransactions ? t("Hide Transactions") : t("Show Transactions")}
          </Button>
        </Box>

        {showTransactions ? (
          <Paper elevation={3}>
            <Box p={2}>
              {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Typography color="error" p={2}>
                  {error}
                </Typography>
              ) : transactions.length === 0 ? (
                <Typography p={2}>{t("No transactions found")}</Typography>
              ) : (
                transactions.map((credit) => (
                  <Paper
                    key={credit.id}
                    elevation={1}
                    sx={{
                      p: 2,
                      mb: 2,
                      borderLeft: `4px solid ${
                        credit.type === "debit"
                          ? theme.palette.info.main
                          : credit.status === "paid"
                          ? theme.palette.success.main
                          : theme.palette.warning.main
                      }`,
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={2}>
                        <Typography variant="body2" color="textSecondary">
                          {t("Transaction ID")}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          #{credit.id}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography variant="body2" color="textSecondary">
                          {t("Date")}
                        </Typography>
                        <Typography variant="body1">
                          {new Date(credit.date).toLocaleDateString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="textSecondary">
                          {t("Client")}
                        </Typography>
                        {(() => {
                          const daysSince = getDaysSince(credit.date);
                          const colors = getAgeColors(daysSince);
                          const phone =
                            credit.clientPhone?.toString() ||
                            clientPhoneById[String(credit.clientId ?? "")] ||
                            "-";
                          return (
                            <Box
                              sx={{
                                display: "block",
                                px: 1.25,
                                py: 0.5,
                                borderRadius: 2,
                                border: `1.5px solid ${colors.border}`,
                                borderLeft: `6px solid ${colors.stripe}`,
                                bgcolor: colors.bg,
                                color: colors.text,
                                width: "100%",
                                maxWidth: { xs: "100%", md: "420px" },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={700}
                                  sx={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    minWidth: 0,
                                  }}
                                >
                                  {credit.clientName}{" "}
                                  {credit.clientPseudo &&
                                    `(${credit.clientPseudo})`}
                                </Typography>

                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    color="text.secondary"
                                    sx={{ whiteSpace: "nowrap" }}
                                  >
                                    {t("Phone")}: {phone}
                                  </Typography>
                                  {daysSince !== null && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 800,
                                        px: 0.75,
                                        py: 0.15,
                                        borderRadius: 999,
                                        border: `1px solid ${alpha(
                                          colors.stripe,
                                          0.7,
                                        )}`,
                                        bgcolor: alpha(colors.stripe, 0.12),
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {daysSince}d
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          );
                        })()}
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography variant="body2" color="textSecondary">
                          {t("Type")}
                        </Typography>
                        <Typography
                          variant="body1"
                          textTransform="capitalize"
                          color={
                            credit.type === "debit" ? "info.main" : "inherit"
                          }
                        >
                          {t(credit.type)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography variant="body2" color="textSecondary">
                          {t("Amount")}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          <Currency locale="en-US" currency="TND">
                            {credit.amount}
                          </Currency>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={1}>
                        {credit.type === "credit" && (
                          <Typography
                            variant="body2"
                            sx={{
                              textTransform: "uppercase",
                              fontWeight: "bold",
                              color:
                                credit.status === "paid"
                                  ? "success.main"
                                  : "warning.main",
                              textAlign: "center",
                              p: 0.5,
                              borderRadius: 1,
                              bgcolor:
                                credit.status === "paid"
                                  ? alpha(theme.palette.success.main, 0.1)
                                  : alpha(theme.palette.warning.main, 0.1),
                            }}
                          >
                            {t(credit.status)}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                ))
              )}
            </Box>
          </Paper>
        ) : (
          <Typography variant="body2" color="textSecondary" mb={2}>
            {t('Click "Show Transactions" to load the detailed list')}
          </Typography>
        )}
        {pagination.totalItems > 0 && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={2}
          >
            <Typography variant="body2" color="textSecondary">
              {t("Showing")} {showingRangeStart}-{showingRangeEnd} {t("of")}{" "}
              {pagination.totalItems}
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <FormControl size="small">
                <InputLabel>{t("Rows per page")}</InputLabel>
                <Select
                  value={String(pagination.pageSize)}
                  label={t("Rows per page")}
                  onChange={handlePageSizeChange}
                >
                  {[12, 24, 50, 100].map((size) => (
                    <MenuItem key={size} value={String(size)}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Pagination
                color="primary"
                count={Math.max(pagination.totalPages, 1)}
                page={page}
                onChange={handlePageChange}
                showFirstButton
                showLastButton
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
